import { setupCollectionGridPromo, scheduleCollectionGridPromoReposition } from '@theme/collection-grid-promo';
import { ThemeEvents } from '@theme/events';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {(() => void) | null} */
  #gridPromoCleanup = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdatePromo);
    this.#setupGridPromo();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdatePromo);
    this.#gridPromoCleanup?.();
    this.#gridPromoCleanup = null;
  }

  #setupGridPromo = () => {
    this.#gridPromoCleanup?.();
    this.#gridPromoCleanup = setupCollectionGridPromo(this.refs.grid);
  };

  #handleFilterUpdatePromo = () => {
    requestIdleCallback(() => this.#setupGridPromo());
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    scheduleCollectionGridPromoReposition(grid);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const selector = event.matches
      ? '[data-grid-layout="desktop-default-option"]'
      : '[data-grid-layout="mobile-option"]';

    const targetElements = this.querySelectorAll(selector);

    targetElements.forEach((targetElement) => {
      if (!(targetElement instanceof HTMLInputElement)) return;
      targetElement.checked = true;
    });

    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
